import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../api/client";
import { tenantPath } from "../utils/tenantContext";

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ExecutiveGRCPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await apiFetch(
          tenantPath(`/grc/executive-summary`)
        );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.detail ||
            "Unable to load executive GRC data."
        );
      }

      setData(responseData);
    } catch (loadError) {
      console.error("Executive GRC error:", loadError);
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleTenantChange = () => {
      loadData();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      handleTenantChange
    );

    return () => {
      window.removeEventListener(
        "wirelesspress-tenant-change",
        handleTenantChange
      );
    };
  }, [loadData]);

  if (loading) {
    return <div className="grc-loading">Loading Executive GRC...</div>;
  }

  const assessment = data?.assessment_metrics || {};
  const risks = data?.risk_metrics || {};
  const remediation = data?.remediation_metrics || {};
  const vendors = data?.vendor_metrics || {};
  const policies = data?.policy_metrics || {};
  const mappings = data?.mapping_metrics || {};

  return (
    <div className="grc-page">
      <section className="grc-header">
        <div>
          <span>WIRELESS PRESS GOVERNANCE, RISK & COMPLIANCE</span>
          <h2>Executive GRC Dashboard</h2>
          <p>
            Unified oversight of compliance, enterprise risk,
            remediation, policies, vendors, and control mappings.
          </p>
        </div>

        <button type="button" onClick={loadData}>
          Refresh GRC
        </button>
      </section>

      {error && <div className="grc-warning">{error}</div>}

      <section className="grc-metrics">
        <article>
          <span>Compliance Score</span>
          <strong>
            {Number(assessment.average_score || 0).toFixed(1)}%
          </strong>
        </article>

        <article>
          <span>Open Risks</span>
          <strong>{risks.open_risks || 0}</strong>
          <small>{risks.high_risks || 0} high</small>
        </article>

        <article>
          <span>Open Remediation</span>
          <strong>{remediation.open_tasks || 0}</strong>
          <small>{remediation.p1_open_tasks || 0} P1</small>
        </article>

        <article>
          <span>Vendors</span>
          <strong>{vendors.total_vendors || 0}</strong>
          <small>
            {vendors.pending_assessments || 0} pending
          </small>
        </article>

        <article>
          <span>Active Policies</span>
          <strong>{policies.active_policies || 0}</strong>
        </article>

        <article>
          <span>Control Mappings</span>
          <strong>{mappings.total_mappings || 0}</strong>
          <small>
            {Number(
              mappings.average_mapping_strength || 0
            ).toFixed(1)}
            % strength
          </small>
        </article>
      </section>

      <section className="grc-grid">
        <article className="grc-panel">
          <h3>Top Enterprise Risks</h3>

          {(data?.top_risks || []).map((risk) => (
            <div className="grc-row" key={risk.id}>
              <div>
                <strong>{risk.risk_number}</strong>
                <span>{risk.title}</span>
              </div>

              <div>
                <strong>{risk.residual_score}</strong>
                <span>{formatLabel(risk.residual_level)}</span>
              </div>
            </div>
          ))}
        </article>

        <article className="grc-panel">
          <h3>Priority Remediation</h3>

          {(data?.top_remediation || []).map((task) => (
            <div className="grc-row" key={task.id}>
              <div>
                <strong>
                  {task.control_identifier || task.priority}
                </strong>
                <span>{task.title}</span>
              </div>

              <div>
                <strong>{task.priority}</strong>
                <span>{formatLabel(task.status)}</span>
              </div>
            </div>
          ))}
        </article>
      </section>

      <section className="grc-panel">
        <h3>Recent Assessments</h3>

        <div className="grc-table-wrapper">
          <table className="grc-table">
            <thead>
              <tr>
                <th>Assessment</th>
                <th>Score</th>
                <th>Maturity</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {(data?.recent_assessments || []).map((item) => (
                <tr key={item.id}>
                  <td>{item.assessment_name}</td>
                  <td>
                    {Number(item.overall_score || 0).toFixed(1)}%
                  </td>
                  <td>{formatLabel(item.maturity_level)}</td>
                  <td>{formatLabel(item.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default ExecutiveGRCPage;
